import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { debounce } from 'lodash';
import type Action from 'lib/jsonApi/Action';
import { useAppDispatch } from 'hooks/reduxHooks';
import type { Link } from 'types/json-api-types';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { GenericActionPayload } from 'types/redux-types';

type UseDebouncedSearchArgs<Params, Query> = {
  debounceSpeed?: number;
  request?: ActionCreatorWithPayload<GenericActionPayload<Params, Query>> | null;
  link?: Link;
  action?: Action | null;
  query?: Query;
  params?: Params;
  onSuccess?: (args?: any) => void;
  onError?: (args?: any) => void;
  autoFetch?: boolean;
};

export const useDebouncedSearch = <Params, Query>({ 
  debounceSpeed = 500, 
  request, 
  link, 
  action, 
  query, 
  params, 
  onSuccess, 
  onError,
  autoFetch = true,
  ...rest 
}: UseDebouncedSearchArgs<Params, Query>) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  
  // Store latest values in refs to avoid recreating debounced function
  const queryRef = useRef<Query | undefined>(query);
  const paramsRef = useRef<Params | undefined>(params);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const restRef = useRef(rest);
  const isMountedRef = useRef(false);

  // Keep refs updated
  useEffect(() => {
    queryRef.current = query;
    paramsRef.current = params;
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
    restRef.current = rest;
  });

  // Stable hash for comparison
  const paramsHash = useMemo(() => JSON.stringify(params), [params]);
  const queryHash = useMemo(() => JSON.stringify(query), [query]);

  // Create stable debounced function
  const debouncedRequest = useMemo(
    () => debounce((search?: any) => {
      if (!request) {
        setLoading(false);
        return;
      }

      const requestParams = {
        query: { ...queryRef.current, ...(search || {}) },
        params: { ...paramsRef.current, ...(search || {}) },
        onSuccess: (res: any) => {
          const { data } = res ?? {};
          setLoading(false);
          onSuccessRef.current?.({ data });
        },
        onError: (res: any) => {
          const { error } = res ?? {};
          setLoading(false);
          onErrorRef.current?.({ error });
        },
        ...restRef.current,
      };

      if (link) {
        dispatch(request({ ...requestParams, link }));
      } else if (action) {
        dispatch(request({ ...requestParams, action }));
      } else {
        dispatch(request(requestParams));
      }
    }, debounceSpeed),
    [debounceSpeed, request, link, action, dispatch]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedRequest.cancel();
      setLoading(false);
    };
  }, [debouncedRequest]);

  const requestResource = useCallback(
    (search?: string | null) => {
      setLoading(true);
      debouncedRequest(search ? { search } : undefined);
    },
    [debouncedRequest]
  );

  // Immediate fetch (no debounce) for initial load
  const requestResourceImmediate = useCallback(() => {
    if (!request) return;
    
    setLoading(true);
    
    const requestParams = {
      query: queryRef.current,
      params: paramsRef.current,
      onSuccess: (res: any) => {
        const { data } = res ?? {};
        setLoading(false);
        onSuccessRef.current?.({ data });
      },
      onError: (res: any) => {
        const { error } = res ?? {};
        setLoading(false);
        onErrorRef.current?.({ error });
      },
      ...restRef.current,
    };

    if (link) {
      dispatch(request({ ...requestParams, link }));
    } else if (action) {
      dispatch(request({ ...requestParams, action }));
    } else {
      dispatch(request(requestParams));
    }
  }, [request, link, action, dispatch]);

  // Auto-fetch logic
  useEffect(() => {
    if (!autoFetch) return;

    if (!isMountedRef.current) {
      // Initial mount - fetch immediately without debounce
      isMountedRef.current = true;
      requestResourceImmediate();
    } else {
      // Subsequent changes - debounce
      requestResource();
    }
  }, [autoFetch, queryHash, paramsHash, requestResource, requestResourceImmediate]);

  return { loading, requestResource };
};